import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { CORE_API_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const API_BASE_URL = `${CORE_API_BASE_URL}/health-check`;

export default function useHealthChecks(page = 0, size = 10) {
  const authToken = sessionStorage.getItem("authToken");
  const [healthChecks, setHealthChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ totalPages: 0, totalElements: 0 });

  const normalizeHealthCheck = useCallback((item) => {
    if (!item) return item;

    const enabled =
      typeof item.active === "boolean" ? item.active : !!item.isEnabled;
    const healthy =
      typeof item.isHealthy === "boolean"
        ? item.isHealthy
        : String(item.isHealthy).toLowerCase() === "true"
        ? true
        : String(item.isHealthy).toLowerCase() === "false"
        ? false
        : null;

    return {
      ...item,
      active: enabled,
      isEnabled: enabled,
      isHealthy: healthy,
      lastCheckedAt: item.lastCheckedAt ?? item.lastUpdatedAt ?? null,
      lastResponseMessage:
        item.lastResponseMessage ?? item.lastCheckMessage ?? null,
    };
  }, []);

  const handleAxiosErrors = (err, action = "operation") => {
    const message =
      err.response?.data?.message || err.message || `Failed to ${action}`;
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  const withNoCache = (params = {}) => ({
    ...params,
    _ts: Date.now(),
  });

  const fetchAllHealthChecks = useCallback(
    async (customPage = page) => {
      if (!authToken) return;
      setLoading(true);
      try {
        const res = await apiClient.get(API_BASE_URL, {
          params: withNoCache({ page: customPage, size, sort: "createdAt,desc" }),
        });

        if (res.data.content) {
          const normalizedList = (res.data.content || []).map(normalizeHealthCheck);
          setHealthChecks(normalizedList);
          setPagination({
            totalPages: res.data.totalPages,
            totalElements: res.data.totalElements,
          });
          return normalizedList;
        } else {
          const normalizedList = (res.data || []).map(normalizeHealthCheck);
          setHealthChecks(normalizedList);
          setPagination({
            totalPages: 1,
            totalElements: (res.data || []).length,
          });
          return normalizedList;
        }
      } catch (error) {
        handleAxiosErrors(error, "fetch all health checks");
        return [];
      } finally {
        setLoading(false);
      }
    },
    [authToken, normalizeHealthCheck, page, size]
  );

  const searchHealthChecks = useCallback(
    async (
      query = "",
      status = "ALL",
      severity = "ALL",
      enabled = "ALL",
      searchPage = 0
    ) => {
      if (!authToken) return;
      setLoading(true);
      try {
        const params = withNoCache({ page: searchPage, size, sort: "createdAt,desc" });
        if (status === "HEALTH") params.isHealthy = true;
        if (status === "NOT_HEALTHY") params.isHealthy = false;

        const statusAsSeverity =
          status && status !== "ALL" && status !== "HEALTH" && status !== "NOT_HEALTHY"
            ? status
            : "ALL";
        const resolvedSeverity =
          severity && severity !== "ALL" ? severity : statusAsSeverity;
        if (resolvedSeverity && resolvedSeverity !== "ALL") {
          params.severity = resolvedSeverity;
        }
        if (enabled === "ENABLED") params.isEnabled = true;
        if (enabled === "DISABLED") params.isEnabled = false;
        if (query?.trim().length >= 3) params.q = query.trim();

        const isFiltered =
          typeof params.isHealthy === "boolean" ||
          Boolean(params.severity) ||
          typeof params.isEnabled === "boolean" ||
          Boolean(params.q);
        const endpoint = isFiltered ? `${API_BASE_URL}/search` : API_BASE_URL;
        const res = await apiClient.get(endpoint, { params });

        const checks = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.content)
          ? res.data.content
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

        setHealthChecks(checks.map(normalizeHealthCheck));
        setPagination({
          totalPages: res.data?.totalPages || 1,
          totalElements: res.data?.totalElements || checks.length || 0,
        });
      } catch (error) {
        handleAxiosErrors(error, "search health checks");
      } finally {
        setLoading(false);
      }
    },
    [authToken, normalizeHealthCheck, size]
  );

  const addHealthCheck = useCallback(
    async (payload) => {
      if (!authToken) return;
      try {
        const res = await apiClient.post(`${API_BASE_URL}/register`, payload);
        const normalizedCreated = normalizeHealthCheck(res.data || {});
        // Refresh from backend so newly created rows always have server IDs.
        const refreshed = await fetchAllHealthChecks(0);
        if (normalizedCreated?.id) {
          return normalizedCreated;
        }
        const matched =
          (refreshed || []).find(
            (item) =>
              item?.name === payload?.name &&
              item?.url === payload?.url &&
              item?.oliveebApp === payload?.oliveebApp
          ) || (refreshed || [])[0];
        toast.success("Health check added successfully");
        return matched || normalizedCreated || res.data;
      } catch (error) {
        handleAxiosErrors(error, "add health check");
      }
    },
    [authToken, fetchAllHealthChecks]
  );

  const updateHealthCheck = useCallback(
    async (id, payload) => {
      if (!authToken) return;
      try {
        const res = await apiClient.patch(`${API_BASE_URL}/${id}`, payload);
        const responseData = res?.data;
        const hasResponseBody =
          responseData &&
          typeof responseData === "object" &&
          Object.keys(responseData).length > 0;
        let finalData = hasResponseBody ? responseData : { ...payload, id };
        setHealthChecks((prev) =>
          prev.map((item) => {
            if (String(item.id) !== String(id)) return item;
            const mergedSource = hasResponseBody
              ? { ...item, ...responseData, id: item.id ?? id }
              : { ...item, ...payload, id: item.id ?? id };
            return normalizeHealthCheck(mergedSource);
          })
        );

        // Reconcile with authoritative backend state to avoid partial PATCH response drift.
        const reconcileById = async () => {
          const detailsRes = await apiClient.get(`${API_BASE_URL}/${id}`, {
            params: withNoCache(),
          });
          const authoritative = normalizeHealthCheck(detailsRes?.data || null);
          if (authoritative) {
            setHealthChecks((prev) =>
              prev.map((item) =>
                String(item.id) === String(id) ? { ...item, ...authoritative } : item
              )
            );
            finalData = detailsRes?.data || finalData;
          }
        };

        try {
          await reconcileById();
          // Some backends finalize health diagnostics milliseconds after the toggle write.
          await new Promise((resolve) => setTimeout(resolve, 800));
          await reconcileById();
        } catch (detailsErr) {
          // Non-fatal: keep optimistic/merged state if detail refresh fails.
          console.warn("health check reconcile warning:", detailsErr);
        }

        toast.success("Health check updated successfully");
        return finalData;
      } catch (error) {
        handleAxiosErrors(error, "update health check");
      }
    },
    [authToken, normalizeHealthCheck]
  );

  const deleteHealthCheck = useCallback(
    async (id) => {
      if (!authToken) return;
      try {
        await apiClient.delete(API_BASE_URL, { params: { id } });
        setHealthChecks((prev) => prev.filter((item) => item.id !== id));
        toast.success("Health check deleted");
      } catch (error) {
        handleAxiosErrors(error, "delete health check");
      }
    },
    [authToken]
  );

  const runProbe = useCallback(
    async (payload) => {
      if (!authToken) return;
      setLoading(true);
      try {
        const res = await apiClient.post(`${API_BASE_URL}/probe`, payload);
        toast.success("Probe test successful");
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "run probe test");
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  const fetchHealthCheckById = useCallback(
    async (id) => {
      if (!authToken || !id) return;
      setLoading(true);
      try {
        const res = await apiClient.get(`${API_BASE_URL}/${id}`, {
          params: withNoCache(),
        });
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "fetch health check details");
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  useEffect(() => {
    if (authToken) fetchAllHealthChecks(page);
  }, [authToken, fetchAllHealthChecks, page]);

  return {
    healthChecks,
    loading,
    pagination,
    fetchAllHealthChecks,
    searchHealthChecks,
    fetchHealthCheckById,
    addHealthCheck,
    updateHealthCheck,
    deleteHealthCheck,
    runProbe,
  };
}
